import React from 'react';

import { DisclosurePanel } from '../components/AppShellPrimitives';
import CopyValue from '../components/CopyValue';
import { formatEvidenceDateTime } from '../evidencePosture';
import { reliabilityVerdictChipClassName } from '../reliability';

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function analysisWindowLabel(window) {
  if (!window || !window.started_at || !window.ended_at) {
    return 'Waiting for completed analysis.';
  }
  return `${formatEvidenceDateTime(window.started_at)} to ${formatEvidenceDateTime(window.ended_at)}`;
}

export default function WorkflowsListView({ selectedSystem, workflowSummaries, banner, findingsHref }) {
  const workflowsWithActiveFindings = workflowSummaries.filter((summary) => summary.active_finding_count > 0).length;
  const workflowsNeedingValidation = workflowSummaries.filter(
    (summary) => summary.reliability_verdict?.overall_verdict === 'operationally_stable_validation_needed'
  ).length;
  const lowConfidenceWorkflows = workflowSummaries.filter(
    (summary) => summary.reliability_verdict?.overall_verdict === 'low_confidence'
  ).length;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        {banner}
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Workflow surfaces</p>
            <h2>Workflows</h2>
            <p className="panel-copy">Stay workflow-first: review run-status trends, active runtime patterns, and direct drill-down links into findings.</p>
          </div>
          <div className="form-actions">
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}`}>
              Overview
            </a>
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings`}>
              Review findings
            </a>
          </div>
        </div>

        <div className="findings-summary-grid">
          <div className="detail-card summary-card">
            <span className="summary-card-label">Tracked workflows</span>
            <strong>{workflowSummaries.length}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Hotspots</span>
            <strong>{workflowsWithActiveFindings}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Need validation</span>
            <strong>{workflowsNeedingValidation}</strong>
          </div>
          <div className="detail-card summary-card summary-card-muted">
            <span className="summary-card-label">Low confidence</span>
            <strong>{lowConfidenceWorkflows}</strong>
          </div>
        </div>

        {!workflowSummaries.length ? (
          <div className="empty-state-card">
            <strong>No workflows available yet</strong>
            <p>Create or onboard a workflow to make this surface useful.</p>
          </div>
        ) : (
          <>
            <section className="findings-priority-band workflow-priority-band">
              <div className="chapter-header">
                <div>
                  <p className="chapter-kicker">Review queue</p>
                  <h3>What needs attention first</h3>
                </div>
              </div>
              <p className="panel-copy">
                {workflowsWithActiveFindings} workflows currently show active runtime pressure, and {workflowsNeedingValidation} still need stronger targeted validation coverage.
              </p>
            </section>

            <div className="stack workflow-summary-stack">
            {workflowSummaries.map((summary) => (
              <article className="detail-card workflow-summary-card workflow-review-card" key={summary.workflow.id}>
                <div className="workflow-summary-header">
                  <div>
                    <p className="card-kicker">{summary.workflow.status}</p>
                    <h3>{summary.workflow.name}</h3>
                  </div>
                  <a className="pill-link" href={`#/systems/${selectedSystem.id}/workflows/${summary.workflow.id}`}>
                    View workflow
                  </a>
                </div>

                <p>{summary.workflow.description || 'No workflow description provided yet.'}</p>

                <DisclosurePanel title="Workflow details" className="workflow-target-context">
                  <CopyValue label="Workflow ID" value={summary.workflow.id} />
                </DisclosurePanel>

                <div className="workflow-metric-grid">
                  <div className="detail-card detail-stat-card">
                    <strong>{formatPercent(summary.success_rate)}</strong>
                    <p>Success rate</p>
                  </div>
                  <div className="detail-card detail-stat-card">
                    <strong>{summary.active_finding_count}</strong>
                    <p>Active findings</p>
                  </div>
                  <div className="detail-card detail-stat-card">
                    <strong>{summary.analysis_window.run_count}</strong>
                    <p>Runs in window</p>
                  </div>
                </div>

                <div className="workflow-insight-grid">
                  {summary.reliability_verdict ? (
                    <section className="detail-card">
                      <div className="workflow-mode-header">
                        <strong>Reliability verdict</strong>
                        <span className={reliabilityVerdictChipClassName(summary.reliability_verdict.overall_verdict)}>
                          {summary.reliability_verdict.label}
                        </span>
                      </div>
                      <p>{summary.reliability_verdict.summary}</p>
                      <p className="overview-note">{summary.reliability_verdict.reason}</p>
                    </section>
                  ) : null}

                  <section className="detail-card">
                    <strong>Validation coverage</strong>
                    <p>{summary.validation_coverage.summary}</p>
                    <p className="overview-note">{summary.validation_coverage.reason}</p>
                  </section>

                  <section className="detail-card">
                    <strong>Improvement proof</strong>
                    <p>{summary.validation_proof.summary}</p>
                    {summary.validation_proof.low_data_reason ? (
                      <p className="overview-note">{summary.validation_proof.low_data_reason}</p>
                    ) : null}
                  </section>
                </div>

                <div className="workflow-summary-meta labeled-stat-list">
                  <div className="labeled-stat-row">
                    <span>Trend</span>
                    <strong>{summary.trend.summary}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Analysis window</span>
                    <strong>{analysisWindowLabel(summary.analysis_window)}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Last analysed</span>
                    <strong>{formatEvidenceDateTime(summary.evidence_posture?.last_analysed_at)}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Snapshot refreshed</span>
                    <strong>{formatEvidenceDateTime(summary.last_refreshed_at)}</strong>
                  </div>
                </div>
                {summary.trend.low_data_reason ? <p className="overview-note">{summary.trend.low_data_reason}</p> : null}

                {summary.top_failure_modes.length ? (
                  <div className="stack compact-stack">
                    <strong>Top runtime patterns</strong>
                    {summary.top_failure_modes.map((mode) => (
                      <div className="detail-card workflow-mode-card" key={`${summary.workflow.id}-${mode.mode_key}`}>
                        <div className="workflow-mode-header">
                          <strong>{mode.title}</strong>
                          <span className="chip">{mode.severity}</span>
                        </div>
                        <p>{mode.summary}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-card">
                    <strong>No dominant runtime patterns yet</strong>
                    <p>This workflow does not currently have active findings or non-success runs in the latest analysis window.</p>
                  </div>
                )}

                <div className="chip-row">
                  <a className="chip chip-emphasis" href={`#/systems/${selectedSystem.id}/workflows/${summary.workflow.id}`}>
                    View workflow
                  </a>
                  <a className="chip chip-emphasis" href={findingsHref(selectedSystem.id, { workflowId: summary.workflow.id })}>
                    Open workflow findings
                  </a>
                </div>
              </article>
            ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
